import { nanoid } from 'nanoid';
import { useStore } from './store';

/**
 * Cross-window state synchronisation for Skematiq's pop-out feature.
 *
 * Skematiq can pop a single sheet out into a new Electron window so the user
 * can keep, e.g., the Block Diagram on one monitor and a Schematic on the
 * other. Both windows share the same project state and BOTH can edit; this
 * module is the transport that keeps them aligned.
 *
 * Protocol (BroadcastChannel "skema-sync"):
 *   • Each window has a unique WINDOW_ID generated at launch and tags every
 *     message it sends with that id; receivers ignore their own messages.
 *   • A pop-out window sends `{ type: 'request-state' }` on mount. Existing
 *     windows reply with the current `{ type: 'state', meta, sheets }`.
 *   • Any window that detects a local edit broadcasts a `state` message
 *     after a 50 ms debounce.
 *   • Receiving a `state` message applies it via `syncFromRemote(meta,
 *     sheets)` — which deliberately leaves per-window state (`activeSheetId`,
 *     selection, focus) alone so each window keeps its own viewing context.
 *   • A re-entrancy flag `applyingRemote` prevents the apply→subscribe→
 *     broadcast→apply loop that would otherwise ping-pong forever.
 *
 * IMPORTANT: a window must NEVER broadcast its initial default state on
 * mount — a freshly-opened popout has a "blank project" in its store, and
 * if it broadcast that, the main window would happily replace its real
 * project with the popout's blank one. The request/response above avoids
 * that trap.
 */

const CHANNEL_NAME = 'skema-sync';
export const WINDOW_ID: string = nanoid(8);

let channel: BroadcastChannel | null = null;
let applyingRemote = false;

interface StateMessage {
  type: 'state';
  from: string;
  meta: ReturnType<typeof useStore.getState>['meta'];
  sheets: ReturnType<typeof useStore.getState>['sheets'];
}

interface RequestStateMessage {
  type: 'request-state';
  from: string;
}

type SyncMessage = StateMessage | RequestStateMessage;

export function setupWindowSync(): () => void {
  if (typeof BroadcastChannel === 'undefined') return () => {};
  channel = new BroadcastChannel(CHANNEL_NAME);

  const sendStateNow = () => {
    if (!channel) return;
    const s = useStore.getState();
    const payload: StateMessage = {
      type: 'state',
      from: WINDOW_ID,
      meta: s.meta,
      sheets: s.sheets,
    };
    channel.postMessage(payload);
  };

  let pendingTimer: ReturnType<typeof setTimeout> | null = null;
  const broadcastEdit = () => {
    if (pendingTimer) clearTimeout(pendingTimer);
    pendingTimer = setTimeout(sendStateNow, 50);
  };

  channel.onmessage = (e: MessageEvent<SyncMessage>) => {
    const msg = e.data;
    if (!msg || msg.from === WINDOW_ID) return;

    if (msg.type === 'request-state') {
      // A popout (or another window) is asking for the current authoritative
      // project state. Reply immediately with whatever we have. If we don't
      // have a real project either, the requester just ends up with our
      // defaults — same as if they had opened standalone, which is fine.
      sendStateNow();
      return;
    }

    if (msg.type === 'state') {
      applyingRemote = true;
      try {
        useStore.getState().syncFromRemote(msg.meta, msg.sheets);
      } finally {
        applyingRemote = false;
      }
      return;
    }
  };

  // Broadcast every local edit (debounced 50 ms). The applyingRemote guard
  // suppresses the bounce-back when WE just received a remote state.
  const unsubscribe = useStore.subscribe(() => {
    if (applyingRemote) return;
    broadcastEdit();
  });

  // If this window is a popout, ask the existing windows for the current
  // project state. We send the request a couple of times to cover the
  // race where the main window's listener isn't quite attached yet.
  const isPopout = readPopoutSheetId() !== null;
  if (isPopout) {
    const req: RequestStateMessage = { type: 'request-state', from: WINDOW_ID };
    channel.postMessage(req);
    setTimeout(() => channel?.postMessage(req), 200);
    setTimeout(() => channel?.postMessage(req), 800);
  }

  return () => {
    unsubscribe();
    if (pendingTimer) clearTimeout(pendingTimer);
    channel?.close();
    channel = null;
  };
}

/** Read the popout sheet ID from the URL hash, if any. */
export function readPopoutSheetId(): string | null {
  if (typeof window === 'undefined') return null;
  const m = window.location.hash.match(/#popout=([\w-]+)/);
  return m ? m[1] : null;
}

/** Open the project in a new window locked to a specific sheet. */
export function openPopoutWindow(sheetId: string): void {
  // Resolve relative to current document so dev (http://localhost:5173/)
  // and packaged (file:///…/dist/index.html) both work. The hash carries
  // the sheet id; the Electron main process is configured to allow this
  // URL pattern through its windowOpenHandler.
  const url = `${window.location.pathname}#popout=${encodeURIComponent(sheetId)}`;
  const width = Math.min(window.screen?.availWidth ?? 1800, 1800);
  const height = Math.min(window.screen?.availHeight ?? 1000, 1000);
  window.open(
    url,
    `skema-popout-${sheetId}`,
    `width=${width},height=${height},resizable=yes,scrollbars=yes`,
  );
}
